<!--
  @copyright 汽车商贸管理平台 V1.0
  @year 2026
  @description BatchInventory 批量盘点（同一订单多台车：连拍登记证书/行驶证等 → 百度OCR识别车架号 → 人工确认 → 批量归类保存）
-->
<template>
  <div class="batch">
    <van-nav-bar
      title="批量盘点"
      left-arrow
      :fixed="true"
      placeholder
      @click-left="$router.back()"
    />

    <div class="body">
      <div class="order-bar">
        <span class="order-no">{{ orderNo }}</span>
        <span class="total">订单车辆 {{ orderVehicles.length }} 台</span>
      </div>

      <!-- 选择本次拍摄的材料分类 -->
      <div class="cat-bar">
        <div class="cat-title">本次上传分类：</div>
        <div class="cat-opts">
          <span
            class="cat-opt"
            :class="{ active: selCatId === c.分类ID }"
            v-for="c in checkCategories"
            :key="c.分类ID"
            @click="selCatId = c.分类ID"
            >{{ c.分类名称 }}</span
          >
        </div>
      </div>

      <!-- 拍照/相册 连续多选 -->
      <div class="photo-bar">
        <input
          type="file"
          ref="photoInput"
          accept="image/*"
          capture="environment"
          multiple
          style="display: none"
          @change="onPhotos"
        />
        <van-button type="primary" block @click="$refs.photoInput.click()">
          连续拍照/相册（可一次多张）
        </van-button>
        <div class="photo-list" v-if="items.length > 0">
          <div class="photo-item" v-for="(it, idx) in items" :key="idx">
            <img :src="it.url" class="photo-thumb" />
            <div class="photo-del" @click="removeItem(idx)">×</div>
          </div>
        </div>
      </div>

      <!-- 识别 -->
      <div class="btn-row" v-if="items.length > 0">
        <van-button
          type="warning"
          block
          :loading="recognizing"
          :disabled="recognizing"
          @click="recognize"
          >识别车架号（{{ items.length }} 张）</van-button
        >
      </div>

      <!-- 识别结果：人工确认/修正后保存 -->
      <div class="result-list" v-if="items.length > 0 && recognized">
        <div
          class="result-row"
          v-for="(it, idx) in items"
          :key="'r' + idx"
          :class="{ bad: !it.ok }"
        >
          <img :src="it.url" class="res-thumb" />
          <div class="res-main">
            <van-field
              v-model="it.vin"
              label="车架号"
              placeholder="识别结果，可修改"
              size="small"
              @input="updateMatch(it)"
            />
            <div class="res-tip">
              <span v-if="it.error" class="err">{{ it.error }}</span>
              <span v-else-if="it.ok" class="ok"
                >✓ {{ it.matchLabel }}</span
              >
              <span v-else class="warn">未匹配订单内车辆（请修正车架号）</span>
            </div>
          </div>
        </div>

        <div class="save-bar">
          <van-button
            type="success"
            block
            :loading="saving"
            :disabled="saving || saveCount === 0"
            @click="saveMatched"
            >保存已匹配材料（{{ saveCount }} 张）</van-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getCheckCategories,
  getOrderVehicles,
  getPendingTasks,
  batchRecognize,
  uploadVehicleMaterials,
} from "../api/index";
import { Toast } from "vant";

function normVin(v) {
  return (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export default {
  name: "BatchInventory",
  data() {
    return {
      orderNo: "",
      checkCategories: [],
      orderVehicles: [], // [{vin,band,model}]
      vinMap: {}, // 标准化VIN -> 车辆信息
      pendingNoByVin: {}, // vin -> 待盘点记录no（用于齐全后自动完成）
      selCatId: 0,
      items: [], // {file,url,name,vin,error,ok,matchLabel}
      recognizing: false,
      recognized: false,
      saving: false,
    };
  },
  computed: {
    saveCount() {
      return this.items.filter((it) => it.ok).length;
    },
  },
  created() {
    this.orderNo = this.$route.query.orderNo || "";
    this.loadInit();
  },
  methods: {
    async loadInit() {
      try {
        const cRes = await getCheckCategories();
        this.checkCategories = (cRes && cRes.code === 0 && cRes.data) || [];
        if (this.checkCategories.length > 0) {
          this.selCatId = this.checkCategories[0].分类ID;
        }
      } catch (e) {
        console.error("加载分类失败:", e);
      }

      try {
        const vRes = await getOrderVehicles(this.orderNo);
        this.orderVehicles = (vRes && vRes.code === 0 && vRes.data) || [];
        this.vinMap = {};
        this.orderVehicles.forEach((v) => {
          const n = normVin(v.vin);
          if (n) {
            this.vinMap[n] = {
              label: `${v.band || ""} ${v.model || ""}`.trim() || v.vin,
            };
          }
        });
      } catch (e) {
        console.error("加载订单车辆失败:", e);
      }

      // 待盘点记录映射（4类齐全时能自动标记完成）
      try {
        const pRes = await getPendingTasks();
        const list = (pRes && pRes.code === 0 && pRes.data) || [];
        this.pendingNoByVin = {};
        list.forEach((t) => {
          if (t.orderNo === this.orderNo && t.vid) {
            this.pendingNoByVin[normVin(t.vid)] = t.no;
          }
        });
      } catch (e) {
        console.error("加载待盘点失败:", e);
      }
    },
    onPhotos(e) {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      this.$refs.photoInput.value = "";
      Toast.loading({ message: "压缩中...", forbidClick: true, duration: 0 });
      const promises = files.map((f) =>
        this.compressImage(f).then((blob) => {
          let name = f.name;
          if (blob.type === "image/jpeg" && !f.name.match(/\.(jpe?g)$/i)) {
            name = f.name.replace(/\.[^.]+$/, ".jpg");
          }
          return {
            file: new File([blob], name, { type: blob.type }),
            url: URL.createObjectURL(blob),
            name,
            vin: "",
            error: "",
            ok: false,
            matchLabel: "",
          };
        })
      );
      Promise.all(promises).then((arr) => {
        Toast.clear();
        arr.forEach((it) => this.items.push(it));
      });
    },
    removeItem(idx) {
      const it = this.items[idx];
      URL.revokeObjectURL(it.url);
      this.items.splice(idx, 1);
    },
    async recognize() {
      this.recognizing = true;
      Toast.loading({ message: "识别中...", forbidClick: true, duration: 0 });
      try {
        const formData = new FormData();
        this.items.forEach((it) => formData.append("photos", it.file));
        const res = await batchRecognize(formData);
        if (res.code !== 0) {
          throw new Error(res.message || "识别失败");
        }
        const rows = res.data || [];
        this.items.forEach((it, i) => {
          const row = rows[i] || {};
          it.vin = row.vin || "";
          it.error = row.error || "";
          this.updateMatch(it);
        });
        this.recognized = true;
        const miss = this.items.filter((it) => !it.ok && !it.error).length;
        Toast.clear();
        Toast(
          miss > 0
            ? `识别完成：${this.items.length} 张，${miss} 张未匹配需修正`
            : "识别完成"
        );
      } catch (error) {
        Toast.clear();
        Toast.fail((error && error.message) || "识别失败");
        console.error("识别失败:", error);
      } finally {
        this.recognizing = false;
      }
    },
    updateMatch(it) {
      const n = normVin(it.vin);
      if (n && this.vinMap[n]) {
        it.ok = true;
        it.matchLabel = this.vinMap[n].label;
        it.error = "";
      } else {
        it.ok = false;
        it.matchLabel = "";
      }
    },
    async saveMatched() {
      const targets = this.items.filter((it) => it.ok);
      this.saving = true;
      let okCount = 0;
      Toast.loading({
        message: `保存中 0/${targets.length}...`,
        forbidClick: true,
        duration: 0,
      });
      try {
        for (let i = 0; i < targets.length; i++) {
          const it = targets[i];
          Toast.loading({
            message: `保存中 ${i + 1}/${targets.length}...`,
            forbidClick: true,
            duration: 0,
          });
          const formData = new FormData();
          formData.append("recordNo", this.pendingNoByVin[normVin(it.vin)] || "");
          formData.append("orderNo", this.orderNo);
          formData.append("vin", normVin(it.vin));
          formData.append("categoryId", this.selCatId);
          formData.append("remark", "批量盘点");
          formData.append("photos", it.file);
          const res = await uploadVehicleMaterials(formData);
          if (res.code !== 0) {
            Toast.fail(res.message || "保存失败");
            break;
          }
          okCount++;
        }
        Toast.clear();
        Toast.success(`已保存 ${okCount} 张`);
        // 移除已保存项，便于继续下一批
        this.items = this.items.filter((it) => !it.ok);
        this.recognized = false;
        // 更新待盘点映射（被自动完成的车辆已不在待盘点）
        try {
          const pRes = await getPendingTasks();
          const list = (pRes && pRes.code === 0 && pRes.data) || [];
          this.pendingNoByVin = {};
          list.forEach((t) => {
            if (t.orderNo === this.orderNo && t.vid) {
              this.pendingNoByVin[normVin(t.vid)] = t.no;
            }
          });
        } catch (e) {}
      } catch (error) {
        Toast.clear();
        Toast.fail((error && error.message) || "保存失败");
        console.error("保存失败:", error);
      } finally {
        this.saving = false;
      }
    },
    // 压缩照片（最大边长1920）
    compressImage(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const maxSide = 1920;
            let { width, height } = img;
            if (width > maxSide || height > maxSide) {
              if (width > height) {
                height = Math.round((height * maxSide) / width);
                width = maxSide;
              } else {
                width = Math.round((width * maxSide) / height);
                height = maxSide;
              }
            }
            canvas.width = width;
            canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            const isSmallPNG =
              file.type === "image/png" && file.size < 2 * 1024 * 1024;
            const outputType = isSmallPNG ? "image/png" : "image/jpeg";
            canvas.toBlob(
              (blob) => resolve(blob || file),
              outputType,
              isSmallPNG ? undefined : 0.9
            );
          };
          img.onerror = () => resolve(file);
          img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      });
    },
  },
  beforeDestroy() {
    this.items.forEach((it) => URL.revokeObjectURL(it.url));
  },
};
</script>

<style scoped>
.batch {
  max-width: 512px;
  margin: 0 auto;
  min-height: 100vh;
  background: #f5f5f5;
}

.body {
  padding: 12px 16px;
}

.order-bar {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 10px;
}

.order-no {
  flex: 1;
  font-size: 14px;
  font-weight: bold;
  color: #333;
  word-break: break-all;
}

.total {
  font-size: 12px;
  color: #ff7d00;
  white-space: nowrap;
}

.cat-bar {
  background: #fff;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 10px;
}

.cat-title {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.cat-opts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cat-opt {
  font-size: 13px;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 16px;
  padding: 4px 14px;
}

.cat-opt.active {
  color: #fff;
  background: #1989fa;
  border-color: #1989fa;
}

.photo-bar {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
}

.photo-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.photo-item {
  position: relative;
  width: 56px;
  height: 56px;
}

.photo-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.photo-del {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  background: #ff4d4f;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
}

.btn-row {
  margin-bottom: 10px;
}

.result-list {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
}

.result-row {
  display: flex;
  align-items: flex-start;
  border-bottom: 1px solid #f0f0f0;
  padding: 8px 0;
}

.result-row.bad {
  background: #fff8f0;
}

.res-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 10px;
  flex-shrink: 0;
}

.res-main {
  flex: 1;
  min-width: 0;
}

.res-tip {
  font-size: 12px;
  margin-top: 2px;
}

.res-tip .ok {
  color: #07c160;
}

.res-tip .warn {
  color: #ff7d00;
}

.res-tip .err {
  color: #ee0a24;
}

.save-bar {
  margin-top: 12px;
}
</style>
